import React, { MouseEvent } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { Counter } from '@/components/uiKit/Counter/Counter';
import { FilterOption, FiltersValues } from './types';

interface MultiCheckboxItemProps {
  option: FilterOption;
  filtersValues: FiltersValues;
  handleFiltersValuesChange: (filtersValues: FiltersValues) => void;
  filterLabel: string;
  className?: string;
  customItemRender?: (option: FilterOption) => React.ReactNode;
  customCounterRender?: (count: number) => React.ReactNode;
}

const MultiCheckboxItem = ({
  option,
  filtersValues,
  handleFiltersValuesChange,
  filterLabel,
  className,
  customItemRender,
  customCounterRender,
}: MultiCheckboxItemProps) => {
  const currentValues = filtersValues[filterLabel] || [];
  const isChecked = currentValues.includes(option.id);
  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Don't allow changes if option is disabled
    if (option.disabled) return;
    const newValue = isChecked
      ? currentValues.filter((value) => value !== option.id)
      : [...currentValues, option.id];
    handleFiltersValuesChange({
      ...filtersValues,
      [filterLabel]: newValue,
    });
  };
  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex justify-between items-center w-full select-none',
        option.disabled ? 'opacity-30 pointer-events-none' : 'hover:bg-[#e6e8ec] dark:hover:bg-slate-700 cursor-pointer',
        className
      )}
    >
      <div className={`pr-2 opacity-100`}>{<Checkbox checked={isChecked} />}</div>
      <div className="flex justify-between items-center w-full capitalize font-medium gap-4 dm-text-primary">
        {customItemRender ? customItemRender(option) : option.label}
        {option.futureCounter !== undefined &&
          (customCounterRender ? (
            customCounterRender(option.futureCounter)
          ) : (
            <Counter value={option.futureCounter} variant={'tab'} size="lg" className="text-center" />
          ))}
      </div>
    </div>
  );
};

interface MultiCheckboxProps {
  filterOptions: FilterOption[];
  filtersValues: FiltersValues;
  handleFiltersValuesChange: (filtersValues: FiltersValues) => void;
  filterLabel: string;
  className?: string;
  customItemRender?: (option: FilterOption) => React.ReactNode;
  customCounterRender?: (count: number) => React.ReactNode;
}

export const MultiCheckbox = ({
  filterOptions,
  filtersValues,
  handleFiltersValuesChange,
  filterLabel,
  className,
  customItemRender,
  customCounterRender,
}: MultiCheckboxProps) => {
  return (
    <>
      {filterOptions.map((option) => (
        <MultiCheckboxItem
          key={option.id}
          option={option}
          filtersValues={filtersValues}
          handleFiltersValuesChange={handleFiltersValuesChange}
          filterLabel={filterLabel}
          className={className}
          customItemRender={customItemRender}
          customCounterRender={customCounterRender}
        />
      ))}
    </>
  );
};
